/* eslint max-lines: "off" */
import React, {MouseEvent, useCallback, useEffect, useState} from 'react';
import {Helmet} from 'react-helmet';
import {shallowEqual, useDispatch, useSelector} from 'react-redux';

import {modalClose, modalOpen} from '../../actions/pageSetup';
import {updateSearchParamsAndSubmit} from '../../actions/searchParams';
import {CommentsFormData} from '../../components/Product/Comments/CommentsForm';
import ProductInfoGrid from '../../components/Product/ProductInfoGrid/ProductInfoGrid';
import {ReviewInterface} from '../../components/Product/Reviews/IndividualReviewListItem/IndividualReviewListItem';
import UserSubmittedPhotosModal from '../../components/Product/UserSubmittedPhotos/UserSubmittedPhotosModal/UserSubmittedPhotosModal';
import {ErrorBoundary} from '../../components/Reusable/ErrorBoundary/ErrorBoundary';
import Loading from '../../components/Reusable/Loading/Loading';
import {SEARCH_PARAMS_RESET} from '../../constants/ActionTypes';
import {DEFAULT_USER_IMAGE} from '../../constants/Assets';
import {FABRIC, FABRIC_BLANK, HOME_GOOD, SOLID, WALLPAPER} from '../../constants/Codes';
import {MAX_NUMBER_OF_TAGS, NO_MUGSHOT} from '../../constants/Design';
import {VIEWPORT_DESKTOP} from '../../constants/Viewports';
import {ApiHostsEnum} from '../../entities/pageSetup/apiHosts';
import useApiHost from '../../entities/pageSetup/apiHosts/useApiHost';
import useOrigin from '../../entities/pageSetup/origin/useOrigin';
import {translate} from '../../services';
import {CommentObject} from '../../shapes/comments';
import {CrossSellCollection, SolidCrossSellItem} from '../../shapes/crossSell';
import {isDesignResponse} from '../../shapes/design';
import {Locale} from '../../shapes/locale';
import {MeasurementType} from '../../shapes/measurement';
import {isProductType, ProductTypeAbbreviated} from '../../shapes/products';
import {TagsProps} from '../../shapes/tags';
import {CommentPhotoData, PhotoData, ReviewPhotoData} from '../../shapes/uploadedPhotos';
import {hasUserId, UserType} from '../../shapes/user';
import {ViewportType} from '../../shapes/viewport';
import {State} from '../../store/initialState';
import ImageUrls, {SMALL_THUMBNAIL_SIZE} from '../../utils/design';
import getCommentReplies from '../../utils/getCommentReplies';
import getListOfRepliesStatus from '../../utils/getListOfRepliesStatus';
import {queryParamsOverrideUserPreferences} from '../../utils/stateToPropsHelpers';
import {isEmpty, isNotUndefined, objectIsEmpty} from '../../utils/validation';
import fetchComments from '../Design/api/fetchComments';
import fetchPostedComments from '../Design/api/fetchPostedComments';
import fetchRemovedComments from '../Design/api/fetchRemovedComments';
import ModalContainer from '../Modal/ModalContainer';


export interface ProductInfoGridContainerProps {
  productType: string; // from parent
  // eslint-disable-next-line react/no-unused-prop-types
  wallpaperType?: string; // from parent
  homeGoodType?: string; // from parent
  productSize?: string; // from parent
  selectedSubstrate: string; // from parent
  orderItemId?: number; // from parent
  hasNoUserFeedback?: boolean; // from parent
  locale: Locale; // from parent
  fabricDetailsHtml?: { __html: string } | undefined;
  showMeasurement?: boolean;
  wallpaperAvailableSubstrateThumbnail?: string;
  rawItemPrice?: number;
  quantity?: number;
  queryParams?: Record<string, string>;
  productInfoRef?: React.LegacyRef<HTMLElement> | undefined;
  crossSellBarItems?: {[key: string]: {imageSrc: string; name: string; link: string}[]};
  longFabricDescription?: string;
  longDescriptionEmphazised?: string;
  trademarkType?: string;
  handleShopClick?: (pathName?: string) => void;
  fabricPrice?: string;
  sizeQuery?: string;
  crossSellFabricDesigns?: CrossSellCollection[];
  crossSellFabricSolids?: SolidCrossSellItem[];
  solidsPricePer?: number;
  colorCode?: string;
  showOnlyProductInfo?: boolean;
  handleFaqButtonClick?: () => void;
}

export interface ProductInfoGridContainerState {
  measurementSystem: MeasurementType;
  currency: string;
  userId?: number;
  userType?: UserType;
  windowPosition?: number;
  designName?: string;
  designId?: number;
  shortDescription?: string;
  longDescription?: string;
  isAvailableOnHomeGood?: boolean;
  isAvailableOnWallpaper?: boolean;
  isAvailableOnFabric?: boolean;
  designFileName?: string;
  designThumbnail?: string;
  tags?: TagsProps;
  designerUserName?: string;
  designerPublicDesigns?: number;
  designerDescription?: string;
  designerId?: number;
  viewport: ViewportType;
  displayModal?: boolean;
  modalName?: string;
  disableUserFeedback?: boolean;
  reviews: {
    reviews: [];
  };
  commentsFetched: boolean;
  commentsFetching: boolean;
  isMugshot?: boolean;
  designerInfoImageId?: number;
  designerInfoImageFile?: string;
  comments?: CommentObject[];
  isRotatedFatQuarter?: boolean;
}

const ProductInfoGridContainer = ({
  locale, hasNoUserFeedback, homeGoodType, productSize, orderItemId, selectedSubstrate, showMeasurement,
  fabricDetailsHtml, productType, wallpaperAvailableSubstrateThumbnail, rawItemPrice, quantity, productInfoRef,
  crossSellBarItems, longFabricDescription, longDescriptionEmphazised, handleShopClick, fabricPrice,
  crossSellFabricDesigns, crossSellFabricSolids, solidsPricePer, trademarkType, sizeQuery, colorCode, queryParams, showOnlyProductInfo, handleFaqButtonClick
}: ProductInfoGridContainerProps): JSX.Element => {
  const imagesHost = useApiHost(ApiHostsEnum.images);
  const s3host = useApiHost(ApiHostsEnum.s3);
  const gardenHost = useApiHost(ApiHostsEnum.garden);
  const currentOrigin = useOrigin();
  const [parentCommentId, setParentCommentId] = useState<number | undefined>(0);
  const [photoDataIndex, setPhotoDataIndex] = useState(0);
  const [photoDataList, setPhotoDataList] = useState<(CommentPhotoData | ReviewPhotoData)[] | []>([]);
  const [userUploadedPhotoButtonId, setUserUploadedPhotoButtonId] = useState(0);
  const [userUploadedPhotoReplyIsClosed, setUserUploadedPhotoReplyIsClosed] = useState(true);
  const [userUploadedPhotoCommentsReplies, setUserUploadedPhotoCommentsReplies] = useState<(CommentObject | JSX.Element)[]>([]);
  const [slideInModal, setSlideInModal] = useState(false);

  const stateSlice = useSelector((state: State) => {
    const featuredDesign = state.design;
    let designPropsFromState;

    if (isDesignResponse(featuredDesign)) {
      const tagsByLocale = isNotUndefined(featuredDesign.tags) ? featuredDesign.tags[locale] : [];

      designPropsFromState = {
        designName: featuredDesign.name,
        designId: featuredDesign.id,
        shortDescription: featuredDesign.short_description,
        longDescription: featuredDesign.description,
        isAvailableOnHomeGood: featuredDesign.for_sale_as_home_good,
        isAvailableOnWallpaper: featuredDesign.for_sale_as_wallpaper,
        isAvailableOnFabric: featuredDesign.for_sale_as_fabric,
        isRotatedFatQuarter: featuredDesign.for_sale_as_tea_towel, // this includes wall hangings now, both are rotated fat quarters
        designFileName: featuredDesign.filename,
        designThumbnail: featuredDesign.design_thumbnail,
        tags: (tagsByLocale || []).filter((t) => (isNotUndefined(t.slug))).slice(0, MAX_NUMBER_OF_TAGS),
        designerUserName: featuredDesign.designer.screen_name,
        designerPublicDesigns: featuredDesign.designer.public_designs,
        designerDescription: featuredDesign.designer.designer_description,
        designerId: featuredDesign.designer.id,
      };
    }

    let containerProps: ProductInfoGridContainerState = {
      ...queryParamsOverrideUserPreferences(state), // country, measurementSystem, currency, userPreferencesOverridden
      userId: hasUserId(state.user) ? state.user.id : undefined,
      userType: state.user.userType,
      windowPosition: state.pageSetup.windowPosition,

      viewport: state.pageSetup.viewport || VIEWPORT_DESKTOP, // TODO SP-8365 handle undefined state.pageSetup
      displayModal: state.pageSetup.modal.displayModal,
      modalName: state.pageSetup.modal.modalName,

      disableUserFeedback: hasNoUserFeedback,
      reviews: state.reviews,
      commentsFetched: state.comments.isFetched,
      commentsFetching: state.comments.isFetching,
      tags: [], // if defined in the design response, this will be overwritten by the following spread
      ...designPropsFromState
    };

    if (isDesignResponse(featuredDesign) && !objectIsEmpty(featuredDesign.designer.designer_image) && isNotUndefined(featuredDesign.designer.designer_image)) {
      containerProps = {
        ...containerProps,
        isMugshot: !!featuredDesign.designer.designer_image.mugshot,
        designerInfoImageId: featuredDesign.designer.designer_image.id,
        designerInfoImageFile: featuredDesign.designer.designer_image.filename
      };
    }

    if (!objectIsEmpty(state.comments)) {
      containerProps = {
        ...containerProps,
        comments: state.comments.comments
      };
    }

    return containerProps;
  }, shallowEqual);

  if (!stateSlice) {
    return (<Loading message={translate('common.loading')} />); // TODO: SP-8361 How do we handle error cases like this? For now, assuming that state design data has not loaded.
  }

  const {
    measurementSystem, currency, userId, userType, windowPosition, designName, designId, shortDescription, longDescription,
    isAvailableOnHomeGood, isAvailableOnWallpaper, isAvailableOnFabric, designFileName, designThumbnail, tags,
    designerUserName, designerPublicDesigns, designerDescription, designerId, viewport, displayModal, modalName,
    disableUserFeedback, reviews, commentsFetched, commentsFetching, isMugshot, designerInfoImageId,
    designerInfoImageFile, comments, isRotatedFatQuarter
  } = stateSlice;

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const dispatch = useDispatch();
  const dispatchPostedComments = (parentId: number | undefined) => (
    dispatch(fetchPostedComments(parentId))
  );
  const dispatchRemovedComment = (commentId: number) => (
    dispatch(fetchRemovedComments(commentId))
  );
  const dispatchResetAllParams = () => dispatch(updateSearchParamsAndSubmit({}, SEARCH_PARAMS_RESET, currentOrigin)); // TODO - SP-8757 Cleanup SearchParams
  const dispatchCloseModal = () => (
    dispatch(modalClose()));
  const dispatchOpenModal = (name: string) => (
    dispatch(modalOpen({
      modal: {
        displayModal: true,
        modalName: name
      }
    })));

  // eslint-disable-next-line react-hooks/rules-of-hooks
  useEffect(() => {
    if (!disableUserFeedback && isNotUndefined(designId) && isNotUndefined(windowPosition) && windowPosition > 0 && !commentsFetched && !commentsFetching) {
      dispatch(fetchComments());
    }
  }, [designId, windowPosition]);

  const deleteComment = (commentId: number | undefined): void => { // eslint-disable-line
    commentId && dispatchRemovedComment(commentId);
    dispatchCloseModal();
  };

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const getUserSubmittedPhotos = useCallback(() => {
    const userSubmittedPhotoList: PhotoData[] = [];

    reviews && reviews.reviews.forEach((review: ReviewInterface) => {
      if (review.media.photo.length > 0) {
        const reviewPhotoList = review.media.photo;
        const userReview = review;

        reviewPhotoList.forEach((photo: ReviewPhotoData) => {
          userSubmittedPhotoList.push({
            thumbnailPhoto: photo.thumbnailUrl,
            photo: photo.originalUrl,
            caption: photo.caption,
            id: review.id,
            review: userReview,
            title: review.title,
            text: review.text,
            createdAt: review.dateCreated
          });
        });
      }
    });

    if (commentsFetched && isNotUndefined(comments) && !isEmpty(comments)) {
      comments.forEach((comment: CommentObject) => {
        const commentContainsPhoto = comment.link;

        if (commentContainsPhoto) {
          const commentId = comment.id;
          const photoThumbnailUrl = ImageUrls.commentImageUrl(s3host, designId, designFileName, commentId);
          const photoUrl = ImageUrls.commentImageUrl(s3host, designId, designFileName, commentId);
          const userScreenName = comment.screen_name;
          const photoCaption = translate('fabric.userSubmittedPhotos.commentPhotoAltText', {
            user: userScreenName
          });
          const mugshot = comment.mugshot;
          const imageId = (typeof mugshot !== 'string') && mugshot.id;
          const imageFilename = (typeof mugshot !== 'string') && mugshot.filename;
          const mugshotImageUrl = mugshot !== NO_MUGSHOT ?
            ImageUrls.avatarDesignerImageUrl(gardenHost, imageId, imageFilename) : `${imagesHost}/${DEFAULT_USER_IMAGE}`;
          const commentCreatedAt = comment.created_at;
          const commentUserId = comment.user_id;
          const commentParentId = comment.parent_id;
          const deletedUsersComment = comment.deleted;
          const usersComment = deletedUsersComment ? translate('designs.comments.deletedComment') : comment.comment;

          userSubmittedPhotoList.push({
            thumbnailPhoto: photoThumbnailUrl,
            photo: photoUrl,
            caption: photoCaption,
            imageSource: mugshotImageUrl,
            userName: userScreenName,
            createdAt: commentCreatedAt,
            userId: commentUserId,
            parentId: commentParentId,
            id: commentId,
            comment: usersComment,
            deletedComment: deletedUsersComment,
            commentsReplies: userId && userType ? getCommentReplies(imagesHost, gardenHost, comment, userId, locale, userType, deleteComment) : []
          });
        }
      });
    }

    return userSubmittedPhotoList;
  }, [comments, commentsFetched, deleteComment, designFileName, designId, gardenHost, imagesHost, locale, reviews, s3host, userId, userType]);

  // eslint-disable-next-line react-hooks/rules-of-hooks
  useEffect(() => {
    getUserSubmittedPhotos();
  }, [comments, reviews]);

  const submitComment = (data: CommentsFormData) => {
    if (data.comment && Object.keys(data.comment).length > 0) {
      return dispatchPostedComments(parentCommentId);
    }
  };

  const getCommentId = (commentId: number | undefined): void => setParentCommentId(commentId);

  const openUserUploadedImagesModal = (event: MouseEvent<HTMLButtonElement>, photoDataIndex: number, photoDataList: (CommentPhotoData | ReviewPhotoData)[]): void => {
    const isCommentPhotoDataList = (input: (CommentPhotoData | ReviewPhotoData)[]): input is CommentPhotoData[] =>
      (input as CommentPhotoData[])[photoDataIndex].comment !== undefined;
    const commentReplyList = isCommentPhotoDataList(photoDataList) ? photoDataList[photoDataIndex].commentsReplies : [];

    event.preventDefault();
    setPhotoDataIndex(photoDataIndex);
    setPhotoDataList(photoDataList);
    isCommentPhotoDataList(photoDataList) && setUserUploadedPhotoCommentsReplies(commentReplyList);
    dispatchOpenModal('user-uploaded-images');
    setTimeout(() =>
      setSlideInModal(true), 350);
    setSlideInModal(false);
  };

  const assignCommentId = (commentId: number): void => {
    setUserUploadedPhotoButtonId(commentId);
    changeReplyState(commentId, userUploadedPhotoButtonId);
  };

  const changeReplyState = (commentId: number, buttonId: number) => {
    const replyIsOpen = userUploadedPhotoReplyIsClosed === false;
    const userClicksDifferentButton = commentId !== buttonId;

    if (replyIsOpen && userClicksDifferentButton) {
      setUserUploadedPhotoReplyIsClosed(false);
    } else {
      setUserUploadedPhotoReplyIsClosed(!userUploadedPhotoReplyIsClosed);
    }
  };

  const updateCommentReplies = (commentId: number): void => {
    isNotUndefined(comments) && !isEmpty(comments) && comments.forEach((comment: CommentObject) => {
      if (comment.id === commentId) {
        const usersCommentHasChildren = isNotUndefined(comment.children);

        usersCommentHasChildren && userId && userType &&
          setUserUploadedPhotoCommentsReplies(getCommentReplies(imagesHost, gardenHost, comment, userId, locale, userType, deleteComment) as (JSX.Element | CommentObject)[]);
      }
    });
  };

  const handleSwiperSlideChange = (direction: number): void => {
    if ((direction === 1) && ((photoDataIndex + 1) < photoDataList.length)) {
      setPhotoDataIndex(photoDataIndex + 1);
    } else if ((direction === 0) && ((photoDataIndex - 1) > -1)) {
      setPhotoDataIndex(photoDataIndex - 1);
    }
  };

  const productTypeAbbreviated = () => {
    let productTypeAbbreviated: ProductTypeAbbreviated;

    if ([FABRIC, WALLPAPER, SOLID, FABRIC_BLANK].includes(productType) && isProductType(productType)) {
      productTypeAbbreviated = productType;
    } else if (productType.startsWith(HOME_GOOD)) {
      productTypeAbbreviated = HOME_GOOD;
    } else {
      throw new Error(`Invalid productType: ${productType}`);
    }

    return productTypeAbbreviated;
  };

  const addNoIndexMeta = () => {
    const productType = productTypeAbbreviated();
    const isFabricNotForSale = productType === FABRIC && !isAvailableOnFabric;
    const isWallpaperNotForSale = productType === WALLPAPER && !isAvailableOnWallpaper;
    const isHomeGoodNotForSale = productType === HOME_GOOD && !isAvailableOnHomeGood;
    // TODO: SP-13467 - FIX THIS and restore this check
    // const isSolidNotForSale = (productType === SOLID && !isRotatedFatQuarter);

    if (isFabricNotForSale || isWallpaperNotForSale || isHomeGoodNotForSale) {
      return (
        <Helmet
          meta={[
            {
              name: 'robots',
              content: 'noindex',
            },
          ]}
        />
      );
    }
  };

  const validSizeQuery = productSize && sizeQuery && productSize.includes(sizeQuery.toUpperCase()) ? sizeQuery.toUpperCase() : '';
  const designThumbnailURL = designId ? ImageUrls.cipheredDesignThumbnail(gardenHost, designId, designThumbnail, SMALL_THUMBNAIL_SIZE) : undefined;

  return (
    <ErrorBoundary>
      {addNoIndexMeta()}
      <ProductInfoGrid
        selectedSubstrate={selectedSubstrate}
        productTypeAbbreviated={productTypeAbbreviated()}
        productType={productType}
        homeGoodType={homeGoodType}
        measurementSystem={measurementSystem}
        currency={currency}
        locale={locale}
        viewport={viewport}
        userType={userType}
        userId={userId}
        productSize={productSize}
        comments={comments}
        commentsFetched={commentsFetched}
        onSubmit={submitComment}
        onClickReturn={getCommentId}
        deleteComment={deleteComment}
        openUserUploadedImagesModal={openUserUploadedImagesModal}
        designId={designId}
        designerId={designerId}
        designerUserName={designerUserName}
        designerPublicDesigns={designerPublicDesigns}
        designerDescription={designerDescription}
        wallpaperAvailableSubstrateThumbnail={wallpaperAvailableSubstrateThumbnail}
        shortDescription={shortDescription}
        longDescription={longDescription}
        fabricDetailsHtml={fabricDetailsHtml}
        isMugshot={isMugshot}
        designerInfoImageId={designerInfoImageId}
        designerInfoImageFile={designerInfoImageFile}
        tags={tags}
        orderItemId={orderItemId}
        designFileName={designFileName}
        designThumbnailURL={designThumbnailURL}
        designName={designName}
        showMeasurement={showMeasurement}
        isAvailableOnWallpaper={isAvailableOnWallpaper}
        isAvailableOnHomeGood={isAvailableOnHomeGood}
        isAvailableOnFabric={isAvailableOnFabric}
        isRotatedFatQuarter={isRotatedFatQuarter}
        userSubmittedPhotoData={getUserSubmittedPhotos()}
        rawItemPrice={rawItemPrice}
        quantity={quantity}
        disableUserFeedback={disableUserFeedback}
        productInfoRef={productInfoRef}
        crossSellBarItems={crossSellBarItems}
        longFabricDescription={longFabricDescription}
        longDescriptionEmphazised={longDescriptionEmphazised}
        trademarkType={trademarkType}
        handleShopClick={handleShopClick}
        fabricPrice={fabricPrice}
        crossSellFabricDesigns={crossSellFabricDesigns}
        crossSellFabricSolids={crossSellFabricSolids}
        solidsPricePer={solidsPricePer}
        sizeQuery={validSizeQuery}
        colorCode={colorCode}
        queryParams={queryParams}
        dispatchResetAllParams={dispatchResetAllParams}
        showOnlyProductInfo={showOnlyProductInfo}
        handleFaqButtonClick={handleFaqButtonClick}
      />
      {(displayModal && modalName === 'user-uploaded-images') &&
      <ErrorBoundary>
        <ModalContainer
          displayFabricsModal={displayModal}
          showFiltersComponent={false}
          slideInModal={slideInModal}
          modalExtraWide={true}
          modalWithButton={false}
          contents={
            <UserSubmittedPhotosModal
              userId={userId}
              userType={userType}
              viewport={viewport}
              locale={locale}
              photoDataIndex={photoDataIndex}
              photoDataList={photoDataList}
              handleSwiperSlideChange={handleSwiperSlideChange}
              comments={comments}
              onClickReturn={getCommentId}
              onSubmit={submitComment}
              buttonId={userUploadedPhotoButtonId}
              commentReplies={userUploadedPhotoCommentsReplies}
              replyIsClosed={userUploadedPhotoReplyIsClosed}
              updateCommentReplies={updateCommentReplies}
              getListOfRepliesStatus={getListOfRepliesStatus}
              deleteComment={deleteComment}
              assignId={assignCommentId}
              designId={designId} />}
        />
      </ErrorBoundary>}
    </ErrorBoundary>
  );
};

export default ProductInfoGridContainer;
